import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/layout/Layout';
import VideoCard from '../components/news/VideoCard';
import { VideoCardSkeleton } from '../components/news/Skeletons';
import { newsAPI } from '../services/api';
import SEO from '../components/seo/SEO';
import Pagination from '../components/ui/Pagination';

const PAGE_SIZE = 20;

const VizyonSektorPage = () => {
  const [videos, setVideos] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchPage = useCallback(async (page) => {
    setLoading(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const [listRes, countRes] = await Promise.all([
        newsAPI.getVizyonSektor({ limit: PAGE_SIZE, skip }),
        newsAPI.getVizyonSektorCount(),
      ]);
      setVideos(listRes.data || []);
      setTotalCount(countRes.data?.total || 0);
    } catch {
      setVideos([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(currentPage);
    // Scroll-to-top already handled globally on route change; ensure jump on page change too
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, fetchPage]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <Layout>
      <SEO
        title="Vizyon Sektör — İş Dünyasından Röportajlar"
        description="Türkiye'nin önde gelen iş insanları ve sektör liderleriyle özel röportajlar, analizler ve vizyon paylaşımları."
        keywords="vizyon sektör, röportaj, iş dünyası, CEO, liderler, türkiye"
      />
      <div data-testid="vizyon-sektor-page">
        {/* Page Header */}
        <section className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
              VİZYON SEKTÖR
            </h1>
            <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto">
              Türkiye'nin önde gelen iş insanları ve sektör liderlerinden özel röportajlar,
              analizler ve vizyon paylaşımları.
            </p>
          </div>
        </section>

        {/* Video Grid — 1 col mobile, 2 sm, 3 md, 4 lg+ */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <VideoCardSkeleton key={`skeleton-${i}`} />
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-lg">Henüz video eklenmemiş.</p>
            </div>
          ) : (
            <>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6"
                data-testid="vizyon-video-grid"
              >
                {videos.map((video) => (
                  <VideoCard key={`video-${video.id}`} video={video} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-10">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChange={setCurrentPage}
                  />
                  <p
                    className="text-center text-xs text-gray-500 mt-3"
                    data-testid="vizyon-page-summary"
                  >
                    Sayfa {currentPage} / {totalPages} — toplam <strong>{totalCount}</strong> röportaj
                  </p>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </Layout>
  );
};

export default VizyonSektorPage;
